import Alert from "@pathwright/ui/src/components/alert/Alert"
import Button from "@pathwright/ui/src/components/button/Button"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import FullscreenConfirm from "@pathwright/ui/src/components/confirm/FullscreenConfirm"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import View from "@pathwright/ui/src/components/ui/View"
import { useMutationWithDefaults } from "@pathwright/utils/src/graphql/mutation"
import { Form, Formik } from "formik"
import { useEffect, useRef } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  ContextualFeaturePermissionDocument,
  FeatureAction,
  FeatureContextInput,
  FeatureKey,
  FeaturePermission,
  UpdateContextualFeaturePermissionMutationVariables,
  useContextualFeaturePermissionQuery,
  useUpdateContextualFeaturePermissionMutation
} from "../../api/generated"
import { useIntercomContext } from "../../intercom/IntercomProvider"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useFeatureUpdatePermission } from "../permissions"
import { featurePermissionsQueryPropTypes } from "../propTypes"
import { getFeatureInfo, getFeaturePermissionInfo } from "../utils"
import FeatureVisibilitySelect from "./FeatureVisibilitySelect"

const Confirm = ({
  children,
  featureKey,
  permission,
  ...confirmProps
}: {
  children: any
  featureKey: FeatureKey
  permission: FeaturePermission
} & React.ComponentProps<typeof FullscreenConfirm> & {}) => {
  const { t } = useTranslate()
  const { school } = usePathwrightContext()

  return (
    <FullscreenConfirm
      icon="caution-triangle"
      heading={t("Update Visibilty for All Cohorts?")}
      body={t(
        "The {{ feature }} will be visible to {{ audience }} in all cohorts in {{ school }}. (Cohorts with customized visibility settings won't be be updated.)",
        {
          feature: getFeatureInfo(featureKey, t).title,
          audience: getFeaturePermissionInfo(featureKey, permission, t).title,
          school: school.name
        }
      )}
      confirmPrompt={t("Yes, Update Visibility")}
      cancelPrompt={t("Nevermind, go back")}
      stack
      {...confirmProps}
    >
      {children}
    </FullscreenConfirm>
  )
}

export const useFeaturePermission = <T extends FeatureAction>({
  featureKey,
  featureAction,
  context
}: {
  featureKey: FeatureKey
  featureAction: T
  context: FeatureContextInput | null
}) => {
  const { usingPathwrightIntercom } = useIntercomContext()

  // Consumer of mutation provides the permissionInContext variable.
  const variables = {
    featureKey,
    featureAction,
    context
  } satisfies Omit<
    UpdateContextualFeaturePermissionMutationVariables,
    "permissionInContext"
  >

  // ugly way to retain the original permission_in_context for event tracking
  // likely in ApollClient 3.0^ we could simply check the permission.query.previousData
  const originalPermissionRef = useRef<FeaturePermission | null>(null)

  const permission = {
    key: featureAction as T,
    query: useContextualFeaturePermissionQuery({
      variables
    }),
    mutation: useMutationWithDefaults(
      useUpdateContextualFeaturePermissionMutation,
      {
        variables,
        onCompleted: (result) => {
          // tracking an Intercom event for when the user changes a cfp's permission_in_context
          // though only for when Pathwright Intercom is enabled
          if (usingPathwrightIntercom && window.Intercom) {
            const cfp = result.updateContextualFeaturePermission
            if (cfp) {
              const eventData = Object.entries({
                feature: cfp.feature_key,
                action: cfp.feature_action,
                school: cfp.school_id,
                resource: cfp.resource_id,
                cohort: cfp.cohort_id,
                from: originalPermissionRef.current,
                to: cfp.permission_in_context
              }).reduce(
                // remove any null values (potentially resource and cohort)
                (data, [key, value]) =>
                  value ? { ...data, [key]: value } : data,
                {}
              )

              window.Intercom(
                "trackEvent",
                "changed-feature-visibility",
                eventData
              )
            }
          }
        },
        refetchQueries: () => {
          // possible that a CFP for the given context does not yet exist
          // (whether the result of the permission.query is the platform default or an existing CFP from a parent context)
          // let's refetch related queries as the mutation will have created
          // the CFP and we need related UIs to have the latest data
          // const {
          //   id,
          //   resource_id,
          //   cohort_id
          // } = permission.query.data.contextualFeaturePermission

          // if (
          //   !id ||
          //   (context &&
          //     (resource_id !== context.resource_id ||
          //       cohort_id !== context.cohort_id))
          // ) {
          //   return [
          //     "ContextualFeaturePermissions",
          //     {
          //       query: CONTEXTUAL_FEATURE_PERMISSION_QUERY,
          //       variables
          //     }
          //   ]
          // }

          // Simply always refetch the CFP query. The cached data wasn't updating for school
          // context (when context is falsy), but keep in mind this is not a new issue with
          // the upgrade to Apollo Client v3 (was also happening in v2). Probably could alternatively
          // update the cache manually in this case?
          return [
            "ContextualFeaturePermissions",
            {
              query: ContextualFeaturePermissionDocument,
              variables
            }
          ]
        }
      }
    ),
    canUpdate: useFeatureUpdatePermission(variables).hasPermission
  }

  useEffect(() => {
    if (permission.query.data) {
      // ugly again, having to delay slightly before updating originalPermissionRef
      // as the query data changes as a result of mutation BEFORE the onCompleted callback runs
      setTimeout(() => {
        originalPermissionRef.current =
          permission.query.data?.contextualFeaturePermission
            ?.permission_in_context || null
      }, 50)
    }
  }, [permission.query.data])

  return permission
}

// extending permissions for form
const useFeaturePermissions = ({
  featureKey,
  context
}: {
  featureKey: FeatureKey
  context: FeatureContextInput | null
}) => {
  const permissions = {
    [FeatureAction.View]: {
      ...useFeaturePermission({
        featureKey,
        featureAction: FeatureAction.View,
        context
      }),
      getInitialValue: function () {
        if (!this.query.data) return undefined
        return this.query.data.contextualFeaturePermission
          ?.permission_in_context!
      },
      getFormValue: function (values: FormValues) {
        return values[this.key]
      }
    },
    [FeatureAction.UpdateVisibility]: {
      ...useFeaturePermission({
        featureKey,
        featureAction: FeatureAction.UpdateVisibility,
        context
      }),
      getInitialValue: function () {
        if (!this.query.data) return undefined
        return (
          this.query.data.contextualFeaturePermission?.permission_in_context ===
          FeaturePermission.StaffOnly
        )
      },
      getFormValue: function (values: FormValues) {
        return values[this.key]
          ? FeaturePermission.StaffOnly
          : FeaturePermission.AdminOnly
      }
    }
  }

  return permissions
}

const FormSchema = z.object({
  [FeatureAction.View]: z.nativeEnum(FeaturePermission),
  [FeatureAction.UpdateVisibility]: z.boolean()
})

type FormValues = z.infer<typeof FormSchema>

const validator = toFormikValidate(FormSchema)

const FeaturePermissionsForm = ({
  featureKey,
  context
}: {
  featureKey: FeatureKey
  context: FeatureContextInput | null
}) => {
  const formikRef = useRef(null)
  const { t } = useTranslate()
  const { title: featureTitle } = getFeatureInfo(featureKey, t)
  const permissions = useFeaturePermissions({ featureKey, context })

  const { loading, error } = Object.values(permissions).reduce(
    (state, { query }) => ({
      loading: state.loading || query.loading,
      error: state.error || query.error
    }),
    {} as { loading: boolean; error?: Error }
  )

  if (loading) {
    return <LoadingCircle center />
  }

  if (error) {
    return <Alert error={error} />
  }

  // NOTE: always performing an update mutation even though at times
  // there may not exist a CFP–the backend automatically creates if no CFP exists
  // even when the request is to update an existing CFP
  async function handleSubmit(values: FormValues) {
    await Promise.all(
      Object.values(permissions)
        // filter out unchanged permissions
        .filter((config) => values[config.key] !== initialValues[config.key])
        // filter out permissions which user cannot update
        .filter((config) => config.canUpdate)
        .map((config) =>
          config.mutation[0]({
            variables: {
              permissionInContext: config.getFormValue(values) || null
            }
          })
        )
    )
  }

  const initialValues: FormValues = {
    [FeatureAction.View]: permissions[FeatureAction.View].getInitialValue()!,
    [FeatureAction.UpdateVisibility]:
      permissions[FeatureAction.UpdateVisibility].getInitialValue()!
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      // enableReinitialize allows the form to reset to new state after submission
      enableReinitialize
      onSubmit={handleSubmit}
      validate={validator}
    >
      {(form) => {
        const { values, dirty, setFieldValue, resetForm } = form

        return (
          <Form style={{ minWidth: "100%" }}>
            <div /* wrapper for View separate prop to work */>
              <View separate paddingTop paddingBottom>
                <FeatureVisibilitySelect
                  labelWidth={200}
                  label={t("Make visible to")}
                  helperText={t(
                    `Sets the visibility of the {{feature}} for all cohorts.`,
                    {
                      feature: featureTitle
                    }
                  )}
                  alignRight
                  value={values[FeatureAction.View]}
                  onChange={(value: FeaturePermission) =>
                    setFieldValue(FeatureAction.View, value, false)
                  }
                  isDisabled={!permissions[FeatureAction.View].canUpdate}
                  featureKey={featureKey}
                />
              </View>
              {!context && (
                <View separate paddingTop paddingBottom>
                  <ToggleInput
                    labelWidth={260}
                    label={t("Enable staff to customize visibility")}
                    helperText={t(
                      "Enable editors, moderators, and teachers to customize the {{ feature }} visibility for cohorts they have access to.",
                      {
                        feature: featureTitle
                      }
                    )}
                    value={values[FeatureAction.UpdateVisibility]}
                    onChange={(value: boolean) =>
                      setFieldValue(
                        FeatureAction.UpdateVisibility,
                        value,
                        false
                      )
                    }
                    disabled={
                      !permissions[FeatureAction.UpdateVisibility].canUpdate
                    }
                    alignRight
                  />
                </View>
              )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {context ? (
                <SubmitButton styleType="primary" disabled={!dirty} brand>
                  {t("Update Visibility")}
                </SubmitButton>
              ) : (
                <Confirm
                  onConfirm={() => handleSubmit(form.values)}
                  onCancel={() => resetForm()}
                  featureKey={featureKey}
                  permission={permissions[FeatureAction.View].getFormValue(
                    values
                  )}
                >
                  {({ confirm }: any) => (
                    <Button
                      styleType="primary"
                      onClick={confirm}
                      disabled={!dirty}
                      brand
                    >
                      {t("Update Visibility")}
                    </Button>
                  )}
                </Confirm>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

FeaturePermissionsForm.displayName = "FeaturePermissionsForm"

FeaturePermissionsForm.propTypes = featurePermissionsQueryPropTypes

export default FeaturePermissionsForm
